// src/config/env.ts
// Configuración centralizada que replica JavascriptResourceHandler.axd
// Carga variables del .env que antes venían del Web.config

interface BuildInfo {
  version: string;
}

interface JsSettings {
  [key: string]: string | boolean | number;
}

interface Config {
  api: {
    baseUrl: string;
    authUrl: string;
  };
  web: {
    url: string;
    chatUrl: string;
    chatVersion: 'v1' | 'v2';
  };
  tenant: {
    defaultName: string;
  };
  socket: {
    server: string;
  };
  features: {
    debug: boolean;
    enableGridView: boolean;
    enableProjectFile: boolean;
    enableProjectGallery: boolean;
    enableProjectChannel: boolean;
  };
  upload: {
    maxAllowedSize: number;
  };
  support: {
    personIdToPost: number;
    projectId: number;
    mainTenantId: number;
    ticketConfigValueId: number;
    layerId: number;
    email: string;
  };
  cache: {
    enabled: boolean;
    minInterval: number;
  };
  cookies: {
    enabled: boolean;
    minInterval: number;
  };
  urls: {
    releaseNotes: string;
    newReleaseNotes: string;
    documentation: string;
    wiki: string;
    corgeeChatApp: string;
    corgeeMobileApp: string;
  };
  tenantRegistration: {
    enabled: boolean;
    path: string;
    user: string;
    password: string;
    tenancyName: string;
    tenantUrlTemplate: string;
    firstSetupPath: string;
  };
}

// Función para obtener valor del .env con fallback
const getEnvVar = (key: string, defaultValue: string = ''): string =>
{
  return (import.meta.env[key] as string) || defaultValue;
};

// Extrae el tenant del subdominio de la URL actual.
// Ej: "qa.devbot.mx" → "qa", "cgbot.devbot.mx" → "cgbot"
// Si no hay subdominio reconocible (localhost, IP, etc.) devuelve ''.
const getTenantFromUrl = (): string =>
{
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname; // ej: "qa.devbot.mx"
  const parts = hostname.split('.');
  // Solo consideramos subdominio si hay al menos 3 segmentos (sub.domain.tld)
  if (parts.length >= 3)
  {
    return parts[0]!; // "qa"
  }
  return '';
};

// Cuando tenancyNameDefault viene vacío, antepone "https://{tenant}." a la URL base.
// Ej: buildTenantUrl("devbot.mx/services", "qa") → "https://qa.devbot.mx/services"
//     buildTenantUrl("https://devbot.mx/services/", "qa") → "https://qa.devbot.mx/services/"
const buildTenantUrl = (rawUrl: string, tenant: string): string =>
{
  if (!rawUrl || !tenant) return rawUrl;
  const stripped = rawUrl.replace(/^https?:\/\//, ''); // quitar protocolo si lo trae
  return `https://${tenant}.${stripped}`;
};

// Calcular tenant y flag "inferir URLs" una sola vez antes de construir config
const _tenancyNameFromEnv = (import.meta.env['VITE_JS_tenancyNameDefault'] as string) || '';
const _resolvedTenantName = _tenancyNameFromEnv || getTenantFromUrl();
// Solo construir URLs con tenant cuando el .env no especifica tenancyName
const _inferTenantUrls = !_tenancyNameFromEnv;

// Local dev without a tenant subdomain: self-reference via current origin, not the legacy IIS path.
const _defaultWebUrl =
  typeof window !== 'undefined' && !_resolvedTenantName
    ? window.location.origin
    : 'http://localhost/Corgee.WebClient';

// The chat bundles these web components and declares the web app URL as
// webURLCorgee, so it is used before falling back to the local IIS path
const _webUrlFallback =
  (import.meta.env['VITE_JS_webURLCorgee'] as string) || _defaultWebUrl;

const getEnvBoolean = (key: string, defaultValue: boolean = false): boolean =>
{
  const value = import.meta.env[key];
  if (value === undefined || value === null) return defaultValue;
  return String(value).toLowerCase() === 'true';
};

const getEnvNumber = (key: string, defaultValue: number = 0): number =>
{
  const value = import.meta.env[key];
  if (value === undefined || value === null) return defaultValue;
  const parsed = Number(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

// A localhost web URL baked into a deployed build must never be used: fall back to the current origin.
const isLocalhostUrl = (url: string): boolean =>
  /^(https?:\/\/)?(localhost|127\.0\.0\.1)([:/]|$)/i.test(url.trim());

const isRunningOnLocalhost = (): boolean =>
  typeof window === 'undefined' || /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);

const sanitizeWebUrl = (url: string): string =>
  !isRunningOnLocalhost() && isLocalhostUrl(url) ? window.location.origin : url;

// Configuración principal
export const config: Config = {
  api: {
    baseUrl: _inferTenantUrls
      ? buildTenantUrl(
        getEnvVar('VITE_JS_apiURL', 'http://localhost/Corgee.Web'),
        _resolvedTenantName,
      )
      : getEnvVar('VITE_JS_apiURL', 'http://localhost/Corgee.Web'),
    authUrl: getEnvVar('VITE_JS_apiAuthURL', 'http://localhost/Corgee.Web/'),
  },
  web: {
    url: sanitizeWebUrl(
      _inferTenantUrls
        ? buildTenantUrl(
          getEnvVar('VITE_JS_webURL', _webUrlFallback),
          _resolvedTenantName,
        )
        : getEnvVar('VITE_JS_webURL', _webUrlFallback),
    ),
    chatUrl: getEnvVar('VITE_JS_webURLChat', ''),
    // 'v2' -> ChatV2 (React), 'v1' -> Chat (legacy). Set per environment.
    chatVersion: getEnvVar('VITE_JS_ChatVersion', 'v1') === 'v2' ? 'v2' : 'v1',
  },
  tenant: {
    defaultName: _resolvedTenantName || 'Default',
  },
  socket: {
    server: getEnvVar('VITE_JS_SocketServer', 'localhost:8880'),
  },
  features: {
    debug: getEnvBoolean('VITE_DEBUG', false),
    enableGridView: getEnvBoolean('VITE_JS_enableGridView', true),
    enableProjectFile: getEnvBoolean('VITE_JS_enableProjectFile', true),
    enableProjectGallery: getEnvBoolean('VITE_JS_enableProjectGallery', true),
    enableProjectChannel: getEnvBoolean('VITE_JS_enableProjectChannel', false),
  },
  upload: {
    maxAllowedSize: getEnvNumber('VITE_JS_maxAllowedUpload', 20480000),
  },
  support: {
    personIdToPost: getEnvNumber('VITE_JS_SupportPersonIdToPost', 1),
    projectId: getEnvNumber('VITE_JS_SupportProjectId', 7607),
    mainTenantId: getEnvNumber('VITE_JS_SupportMainTenantId', 2),
    ticketConfigValueId: getEnvNumber('VITE_JS_SupportTicketConfigValueId', 21),
    layerId: getEnvNumber('VITE_JS_SupportLayerId', 12),
    email: getEnvVar('VITE_JS_SupportEmail', 'support@cgbot.com'),
  },
  cache: {
    enabled: getEnvBoolean('VITE_JS_CacheEnabled', false),
    minInterval: getEnvNumber('VITE_JS_CacheMinInterval', 1),
  },
  cookies: {
    enabled: getEnvBoolean('VITE_JS_CookiesEnabled', false),
    minInterval: getEnvNumber('VITE_JS_CookiesMinInterval', 2),
  },
  urls: {
    releaseNotes: getEnvVar('VITE_JS_ReleaseNotesUrl', ''),
    newReleaseNotes: getEnvVar('VITE_JS_NewReleaseNotesUrl', ''),
    documentation: getEnvVar(
      'VITE_JS_DocumentationUrl',
      'https://corgee.com/quick-start-tutorials/',
    ),
    wiki: getEnvVar('VITE_JS_Wiki_Link', ''),
    corgeeChatApp: getEnvVar('VITE_JS_CorgeeChatURL', ''),
    corgeeMobileApp: getEnvVar('VITE_JS_CorgeeMobileURL', ''),
  },
  // Tenant registration console (alternate URL, hidden unless enabled in .env)
  tenantRegistration: {
    enabled: getEnvBoolean('VITE_TENANT_REGISTRATION_ENABLED', false),
    path: getEnvVar('VITE_TENANT_REGISTRATION_PATH', '/TenantRegistration'),
    // Temporary fixed registrar account (bundled into the client build, dev/internal use only)
    user: getEnvVar('VITE_TENANT_REGISTRATION_USER', ''),
    password: getEnvVar('VITE_TENANT_REGISTRATION_PASSWORD', ''),
    // Tenancy for the registrar API login. Empty = host database (Corgee admin),
    // the only side with the "Pages.Tenants" permission needed to list tenants
    tenancyName: getEnvVar('VITE_TENANT_REGISTRATION_TENANCY', ''),
    // Template used to build tenant URLs, "{tenant}" is replaced by the tenancy name
    tenantUrlTemplate: getEnvVar('VITE_TENANT_URL_TEMPLATE', 'https://{tenant}.devbot.mx/'),
    // Relative path (from the tenant URL) of the first-time site configuration
    firstSetupPath: getEnvVar('VITE_TENANT_FIRST_SETUP_PATH', 'InitialWizard'),
  },
};

// Build info
export const build: BuildInfo = {
  version: getEnvVar('VITE_BUILD_VERSION', '2.0.0'),
};

// JS Settings object (replica del objeto config en legacy)
export const js = {
  settings: {} as JsSettings,
};

// Función para cargar todas las configuraciones JS_ del .env
const loadJsSettings = (): void =>
{
  const envVars = import.meta.env as Record<string, any>;

  // Cargar todas las variables VITE_JS_* en js.settings
  Object.keys(envVars).forEach(key =>
  {
    if (key.startsWith('VITE_JS_'))
    {
      const settingName = key.replace('VITE_JS_', '');
      let value = envVars[key];

      // Convertir strings "true"/"false" a boolean
      if (value === 'true') value = true;
      else if (value === 'false') value = false;
      // Convertir números
      else if (!isNaN(Number(value)) && value !== '') value = Number(value);

      js.settings[settingName] = value;
    }
  });

  // Agregar algunas propiedades adicionales para compatibilidad
  js.settings.buildVersion = build.version;
  js.settings.appPrefix = 'C2';
  js.settings.loginPath = '#/';
  js.settings.maxAllowedUpload = config.upload.maxAllowedSize;
  // Asegurar que tenancyNameDefault refleje el valor resuelto (URL fallback si estaba vacío)
  js.settings.tenancyNameDefault = config.tenant.defaultName;

  if (config.features.debug)
  {
    //console.table(js.settings);
  }
};

// Función de validación de variables de entorno
export const validateEnv = (): void =>
{
  const requiredEnvVars = ['VITE_JS_apiURL', 'VITE_JS_apiAuthURL'];

  const envVars = import.meta.env as Record<string, any>;
  const missingEnvVars = requiredEnvVars.filter(envVar => !envVars[envVar]);

  if (missingEnvVars.length > 0)
  {
    console.warn('⚠️  Missing required environment variables:', missingEnvVars.join(', '));
    console.warn('Using default values. Check your .env file.');
  }

  // Cargar las configuraciones JS_
  loadJsSettings();

  if (config.features.debug)
  {
  }
};

// Inicializar automáticamente
validateEnv();

// Exportar como global para compatibilidad con código legacy
if (typeof window !== 'undefined')
{
  (window as any).build = build;
  (window as any).js = js;
  (window as any).config = config;

  if (config.features.debug)
  {
  }
}
